import { useEffect, useState } from "react";
import {
  Leaf,
  Package,
  ShoppingCart,
  Warehouse,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { GetallProduct, GetSettings, UpdateSettings } from "../api";
import { Link } from "react-router-dom";
function Dashboard() {
  const [products, setProducts] = useState([]);
  const [gstNo, setGstNo] = useState("");
  const [tempGstNo, setTempGstNo] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchSettings();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await GetallProduct();

      if (res.data.success) {
        setProducts(res.data.products);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await GetSettings();

      if (res.data.success) {
        setGstNo(res.data.settings?.gstNumber || "");
        setTempGstNo(res.data.settings?.gstNumber || "");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSaveGST = async () => {
    try {
      const res = await UpdateSettings({
        gstNumber: tempGstNo,
      });

      if (res.data.success) {
        setGstNo(tempGstNo);
        setIsEditing(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    setTempGstNo(gstNo);
    setIsEditing(false);
  };

  const stats = [
    {
      title: "Total Products",
      value: products.length,
      icon: <Package size={28} />,
      color: "bg-green-500",
    },
    {
      title: "Categories",
      value: new Set(products.map((p) => p.category)).size,
      icon: <Leaf size={28} />,
      color: "bg-emerald-500",
    },
    {
      title: "Total Stock",
      value: products.reduce((acc, item) => acc + Number(item.stock || 0), 0),
      icon: <Warehouse size={28} />,
      color: "bg-orange-500",
    },
    {
      title: "Products",
      value: products.length,
      icon: <ShoppingCart size={28} />,
      color: "bg-blue-500",
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="md:mb-8 mb-4 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        {/* Heading */}
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold">
            Hydroponic Dashboard
          </h1>

          <p className="text-gray-500 mt-2 text-sm md:text-base">
            Manage Hydroponic Products & Inventory
          </p>
        </div>

        {/* GST Card */}
        <div className="bg-white border border-green-200 rounded-xl px-4 py-3 shadow-sm w-full lg:w-[320px]">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">GST No</p>

            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition"
              >
                <Pencil size={16} className="text-green-600" />
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveGST}
                  className="p-1 rounded hover:bg-green-100"
                >
                  <Check size={18} className="text-green-600" />
                </button>

                <button
                  onClick={handleCancel}
                  className="p-1 rounded hover:bg-red-100"
                >
                  <X size={18} className="text-red-600" />
                </button>
              </div>
            )}
          </div>

          {!isEditing ? (
            <h3 className="mt-0 md:mt-2 text-lg sm:text-xl font-bold break-all">
              {gstNo || "N/A"}
            </h3>
          ) : (
            <input
              type="text"
              value={tempGstNo}
              onChange={(e) => setTempGstNo(e.target.value)}
              placeholder="Enter GST Number"
              className="w-full mt-3 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-6">
        {stats.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-sm border p-3 md:p-6"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500">{item.title}</p>

                <h2 className="text-3xl font-bold md:mt-2 mt-0">{item.value}</h2>
              </div>

              <div className={`${item.color} text-white md:p-4 p-1 rounded-xl`}>
                {item.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Products */}
      <div className="bg-white rounded-2xl shadow mt-8 p-4 md:p-6">
  <h2 className="text-xl md:text-2xl font-semibold mb-4">
    Recent Products
  </h2>

  {products.length > 0 ? (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-3 px-3">Product Name</th>
              <th className="text-left px-3">Category</th>
              <th className="text-left px-3">Stock</th>
              <th className="text-left px-3">Price</th>
            </tr>
          </thead>

          <tbody>
            {products.slice(0, 5).map((product) => (
              <tr
                key={product._id}
                className="border-b hover:bg-gray-50"
              >
                <td className="py-3 px-3">
                  <Link
                    to="/dashboard/products"
                    className="text-blue-600 hover:underline"
                  >
                    {product.title}
                  </Link>
                </td>

                <td className="px-3">{product.category}</td>

                <td className="px-3">{product.stock}</td>

                <td className="px-3 font-semibold text-green-600">
                  ₹{product.price}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="space-y-4 md:hidden">
        {products.slice(0, 5).map((product) => (
          <div
            key={product._id}
            className="border rounded-xl p-4 shadow-sm"
          >
            <div className="mb-3">
              <p className="text-xs text-gray-500">
                Product Name
              </p>

              <Link
                to="/dashboard/products"
                className="font-semibold text-blue-600 hover:underline"
              >
                {product.title}
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Category</p>
                <p className="font-medium">
                  {product.category}
                </p>
              </div>

              <div>
                <p className="text-gray-500">Stock</p>
                <p className="font-medium">
                  {product.stock}
                </p>
              </div>

              <div className="col-span-2">
                <p className="text-gray-500">Price</p>
                <p className="font-bold text-green-600 text-lg">
                  ₹{product.price}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  ) : (
    <div className="text-center py-8 text-gray-500">
      No Products Found
    </div>
  )}
</div>
    </div>
  );
}

export default Dashboard;
